import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, ArrowRight, AlertTriangle, User2, Info } from "lucide-react";

/**
 * Modal para trocar o indicador de um cliente.
 * Props:
 *  - open: boolean
 *  - onClose: fn
 *  - client: { id, full_name, indicator_id, indicator_name? }
 *  - onChanged: fn (chamado após sucesso)
 */
export default function ChangeIndicatorModal({ open, onClose, client, onChanged }) {
  const [indicators, setIndicators] = useState([]);
  const [newIndicatorId, setNewIndicatorId] = useState("");
  const [reason, setReason] = useState("");
  const [applyToActive, setApplyToActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = form; 2 = confirmação

  useEffect(() => {
    if (!open) return;
    setStep(1); setNewIndicatorId(""); setReason(""); setApplyToActive(false);
    api.get("/indicators")
      .then(r => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        // Exclui o indicador atual e inativos
        setIndicators(items.filter(i => i.id !== client?.indicator_id && i.active !== false));
      })
      .catch(() => setIndicators([]));
  }, [open, client?.indicator_id]);

  if (!open || !client) return null;

  const newInd = indicators.find(i => i.id === newIndicatorId);
  const currentName = client.indicator_name || "(sem indicador atual)";

  const submit = async () => {
    if (!newIndicatorId) return toast.error("Selecione o novo indicador");
    if (reason.trim().length < 3) return toast.error("Informe o motivo da troca (mín. 3 caracteres)");
    setLoading(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/change-indicator`, {
        new_indicator_id: newIndicatorId,
        reason: reason.trim(),
        apply_to_active_contract: applyToActive,
      });
      const extras = [];
      if (data.updated_active_contracts?.length) extras.push(`${data.updated_active_contracts.length} contrato(s) ativo(s) reatribuído(s)`);
      if (data.updated_future_commissions_count) extras.push(`${data.updated_future_commissions_count} comissão(ões) futura(s) atualizada(s)`);
      toast.success(
        extras.length ? `Indicador alterado com sucesso — ${extras.join(", ")}.` : "Indicador alterado com sucesso."
      );
      onChanged?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao trocar indicador");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="change-indicator-modal"
    >
      <div
        className="fc-card p-0 w-full max-w-[520px] max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-[#1a1a1a] flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold flex items-center gap-2">
            <User2 size={16} className="text-[#D4AF37]"/> Trocar Indicador
          </h3>
          <button onClick={onClose} className="text-[#737373] hover:text-red-300" data-testid="ci-close"><X size={16}/></button>
        </div>

        {step === 1 && (
          <div className="p-5 space-y-4">
            {/* De → Para */}
            <div className="grid grid-cols-[1fr_28px_1fr] gap-2 items-center">
              <div className="p-3 rounded-md border border-[#1f1f1f] bg-[#0a0a0a]">
                <div className="text-[10px] uppercase text-[#737373]">Atual</div>
                <div className="text-[13px] text-[#E5E5E5] truncate mt-0.5" title={currentName}>{currentName}</div>
              </div>
              <ArrowRight size={16} className="text-[#D4AF37] justify-self-center"/>
              <div className="p-3 rounded-md border border-emerald-500/30 bg-emerald-500/5">
                <div className="text-[10px] uppercase text-emerald-300">Novo</div>
                <div className="text-[13px] text-emerald-100 truncate mt-0.5">
                  {newInd ? newInd.name : <span className="text-[#737373] italic">selecione abaixo</span>}
                </div>
              </div>
            </div>

            <div>
              <label className="text-[10.5px] uppercase text-[#737373] block mb-1.5">Novo indicador *</label>
              <select
                className="fc-input text-[13px]"
                value={newIndicatorId}
                onChange={e => setNewIndicatorId(e.target.value)}
                data-testid="ci-new-indicator"
              >
                <option value="">— selecione —</option>
                {indicators.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.name} {i.commission_percent ? `(${i.commission_percent}%)` : ""}
                  </option>
                ))}
              </select>
              {indicators.length === 0 && (
                <div className="text-[10.5px] text-amber-300 mt-1">Nenhum outro indicador ativo disponível.</div>
              )}
            </div>

            <div>
              <label className="text-[10.5px] uppercase text-[#737373] block mb-1.5">Motivo da alteração *</label>
              <textarea
                rows={3}
                className="fc-input text-[13px]"
                placeholder="Ex.: Cliente solicitou realocação, indicador anterior desligado, redistribuição de carteira..."
                value={reason}
                onChange={e => setReason(e.target.value)}
                data-testid="ci-reason"
              />
              <div className="text-[10.5px] text-[#737373] mt-1">{reason.length}/500 caracteres · registro imutável na auditoria</div>
            </div>

            <div>
              <label className="text-[10.5px] uppercase text-[#737373] block mb-2">Como aplicar esta alteração? *</label>
              <div className="space-y-1.5">
                <label className="flex items-start gap-2 p-2.5 rounded-md border border-[#1f1f1f] hover:border-[#3a3a3a] cursor-pointer" data-testid="ci-only-new">
                  <input type="radio" checked={!applyToActive} onChange={() => setApplyToActive(false)} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-[12.5px] text-[#E5E5E5]">Apenas para novos contratos</div>
                    <div className="text-[11px] text-[#A3A3A3] mt-0.5">Contratos ativos permanecem com o indicador atual. Recomendado.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-2.5 rounded-md border border-[#1f1f1f] hover:border-[#3a3a3a] cursor-pointer" data-testid="ci-also-active">
                  <input type="radio" checked={applyToActive} onChange={() => setApplyToActive(true)} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-[12.5px] text-[#E5E5E5]">Também aplicar ao contrato ativo</div>
                    <div className="text-[11px] text-[#A3A3A3] mt-0.5">Reatribui o(s) contrato(s) ativo(s) e as comissões futuras. Comissões já pagas não são alteradas.</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="text-[11px] text-[#737373] flex items-start gap-2 p-2.5 rounded-md bg-[#0a0a0a] border border-[#1f1f1f]">
              <Info size={12} className="mt-0.5 flex-shrink-0"/>
              <div>Contratos <strong>já encerrados</strong> ou <strong>quitados</strong> nunca são alterados — permanecem com o indicador original.</div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="fc-btn-ghost text-xs">Cancelar</button>
              <button
                onClick={() => setStep(2)}
                disabled={!newIndicatorId || reason.trim().length < 3}
                className="fc-btn-primary text-xs disabled:opacity-40"
                data-testid="ci-next"
              >
                Revisar
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-5 space-y-3">
            <div className="p-3 rounded-md border border-amber-500/30 bg-amber-500/5 text-[12.5px] text-amber-100 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
              <div><strong>Confirme a alteração.</strong> Esta ação será registrada na auditoria financeira e não pode ser desfeita — apenas revertida com nova troca.</div>
            </div>

            <div className="space-y-2 text-[12.5px]">
              <div className="flex justify-between border-b border-[#1a1a1a] pb-1.5">
                <span className="text-[#737373]">Cliente</span>
                <span className="text-[#E5E5E5] font-medium">{client.full_name}</span>
              </div>
              <div className="flex justify-between border-b border-[#1a1a1a] pb-1.5">
                <span className="text-[#737373]">Indicador atual</span>
                <span className="text-[#A3A3A3]">{currentName}</span>
              </div>
              <div className="flex justify-between border-b border-[#1a1a1a] pb-1.5">
                <span className="text-[#737373]">Novo indicador</span>
                <span className="text-emerald-300 font-medium">{newInd?.name}</span>
              </div>
              <div className="flex justify-between border-b border-[#1a1a1a] pb-1.5">
                <span className="text-[#737373]">Aplicação</span>
                <span className={applyToActive ? "text-amber-300" : "text-[#A3A3A3]"}>{applyToActive ? "Contrato ativo + novos" : "Apenas novos contratos"}</span>
              </div>
              <div>
                <div className="text-[#737373] mb-1">Motivo</div>
                <div className="rounded border border-[#1f1f1f] bg-[#0a0a0a] p-2 text-[#E5E5E5] whitespace-pre-wrap">{reason}</div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setStep(1)} className="fc-btn-ghost text-xs" disabled={loading}>Voltar</button>
              <button
                onClick={submit}
                disabled={loading}
                className="fc-btn-primary text-xs flex items-center gap-1.5"
                data-testid="ci-confirm"
              >
                {loading && <Loader2 size={12} className="animate-spin"/>} Confirmar troca
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
